/**
 * Collapsible tree view for browsing database schemas/tables/columns
 */

import { useState } from 'react'
import { ChevronRight, Table2, Columns3 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'
import { useSchemas, useTables } from '@/hooks/use-discovery'
import type { SchemaInfo, TableInfo } from '@/types/discovery'

interface SchemaTreeProps {
  readonly connectionId: string
  readonly onSelectTable: (schema: string, table: string) => void
  readonly selectedSchema?: string
  readonly selectedTable?: string
}

export function SchemaTree({
  connectionId,
  onSelectTable,
  selectedSchema,
  selectedTable,
}: SchemaTreeProps) {
  const { data: schemas, isLoading } = useSchemas(connectionId)
  const [expandedSchemas, setExpandedSchemas] = useState<Set<string>>(new Set())

  const toggleSchema = (schemaName: string) => {
    setExpandedSchemas((prev) => {
      const entries = [...prev]
      return prev.has(schemaName)
        ? new Set(entries.filter((s) => s !== schemaName))
        : new Set([...entries, schemaName])
    })
  }

  if (isLoading) {
    return (
      <div className="space-y-2 p-2">
        {Array.from({ length: 3 }).map((_, i) => (
          <Skeleton key={i} className="h-6 w-full" />
        ))}
      </div>
    )
  }

  if (!schemas || schemas.length === 0) {
    return (
      <p className="p-4 text-sm text-muted-foreground">
        No schemas found. Try testing the connection first.
      </p>
    )
  }

  return (
    <div className="space-y-0.5">
      {schemas.map((schema) => (
        <SchemaNode
          key={schema.schemaName}
          connectionId={connectionId}
          schema={schema}
          isExpanded={expandedSchemas.has(schema.schemaName)}
          onToggle={() => toggleSchema(schema.schemaName)}
          onSelectTable={onSelectTable}
          selectedSchema={selectedSchema}
          selectedTable={selectedTable}
        />
      ))}
    </div>
  )
}

interface SchemaNodeProps {
  readonly connectionId: string
  readonly schema: SchemaInfo
  readonly isExpanded: boolean
  readonly onToggle: () => void
  readonly onSelectTable: (schema: string, table: string) => void
  readonly selectedSchema?: string
  readonly selectedTable?: string
}

function SchemaNode({
  connectionId,
  schema,
  isExpanded,
  onToggle,
  onSelectTable,
  selectedSchema,
  selectedTable,
}: SchemaNodeProps) {
  const { data: tables, isLoading } = useTables(
    isExpanded ? connectionId : undefined,
    isExpanded ? schema.schemaName : undefined
  )

  return (
    <div>
      <Button
        variant="ghost"
        size="sm"
        className="h-7 w-full justify-start px-2 text-sm font-medium"
        onClick={onToggle}
      >
        <ChevronRight
          className={cn(
            'mr-1 h-3.5 w-3.5 transition-transform',
            isExpanded && 'rotate-90'
          )}
        />
        <Columns3 className="mr-1.5 h-3.5 w-3.5 text-muted-foreground" />
        {schema.schemaName}
        <span className="ml-auto text-xs text-muted-foreground">
          {schema.tableCount}
        </span>
      </Button>

      {isExpanded && (
        <div className="ml-4">
          {isLoading ? (
            <div className="space-y-1 py-1">
              {Array.from({ length: 2 }).map((_, i) => (
                <Skeleton key={i} className="ml-4 h-5 w-3/4" />
              ))}
            </div>
          ) : (
            tables?.map((table) => (
              <TableNode
                key={table.tableName}
                table={table}
                schemaName={schema.schemaName}
                isSelected={
                  selectedSchema === schema.schemaName &&
                  selectedTable === table.tableName
                }
                onSelect={() =>
                  onSelectTable(schema.schemaName, table.tableName)
                }
              />
            ))
          )}
        </div>
      )}
    </div>
  )
}

interface TableNodeProps {
  readonly table: TableInfo
  readonly schemaName: string
  readonly isSelected: boolean
  readonly onSelect: () => void
}

function TableNode({ table, isSelected, onSelect }: TableNodeProps) {
  return (
    <Button
      variant="ghost"
      size="sm"
      className={cn(
        'h-7 w-full justify-start px-2 text-sm',
        isSelected && 'bg-accent'
      )}
      onClick={onSelect}
    >
      <Table2 className="mr-1.5 h-3.5 w-3.5 text-muted-foreground" />
      {table.tableName}
      <span className="ml-auto text-xs text-muted-foreground">
        {table.columnCount} cols
      </span>
    </Button>
  )
}
